import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { SISTEMAS_EM_ORDEM, nomeDoSistema } from "../sistemas";
import { IconPlus, IconTrash, IconCheck, IconEdit, IconRepeat } from "../icons";
import ErrorState from "./ErrorState";
import { dataBr } from "./PainelView";

/**
 * RENOVAÇÕES — o que vence e não pode passar batido.
 *
 * Domínio, certificado do site, licença, assinatura. Cada item tem um
 * vencimento e uma repetição; o sistema cria o lembrete sozinho alguns dias
 * antes (ver server/avisos.ts) e, ao marcar "Renovei", a data pula para o
 * próximo período — o controle se mantém sozinho depois de cadastrado.
 *
 * Sem semáforo de cores: o que muda é a FRASE ("vence em 12 dias", "VENCEU há
 * 3 dias") e a ordem — o que vence primeiro fica em cima.
 */

const CATEGORIAS = ["Domínio", "Certificado", "Licença", "Assinatura", "Contrato", "Hospedagem", "Outro"];
const REPETICOES: { v: string; nome: string }[] = [
  { v: "anual", nome: "Todo ano" },
  { v: "semestral", nome: "A cada 6 meses" },
  { v: "trimestral", nome: "A cada 3 meses" },
  { v: "mensal", nome: "Todo mês" },
  { v: "nenhuma", nome: "Não se repete" },
];

const VAZIO = {
  titulo: "",
  categoria: "Domínio",
  fornecedor: "",
  detalhe: "",
  link: "",
  valor: "",
  vence_em: "",
  repetir: "anual",
  avisar_dias: 15,
  sistema: "",
};

/** "vence em 12 dias", "vence hoje", "VENCEU há 3 dias". */
function quando(r: any): string {
  if (!r.ativo) return "desligada";
  if (r.dias < 0) return "VENCEU há " + Math.abs(r.dias) + (Math.abs(r.dias) === 1 ? " dia" : " dias");
  if (r.dias === 0) return "vence hoje";
  if (r.dias === 1) return "vence amanhã";
  return "vence em " + r.dias + " dias";
}

export default function RenovacoesView() {
  const [linhas, setLinhas] = useState<any[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState<any | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setLinhas((await api.renovacoes.listar()) as any[]);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível carregar as renovações.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function salvar() {
    if (!form?.titulo?.trim() || !form?.vence_em) {
      alert("Escreva o que vence e a data do vencimento.");
      return;
    }
    setSalvando(true);
    try {
      if (form.id) await api.renovacoes.atualizar(form.id, form);
      else await api.renovacoes.criar(form);
      setForm(null);
      await carregar();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  async function renovar(r: any) {
    const sugerida = prompt(
      "Renovar " + r.titulo + ".\n\nAté quando vale agora? (deixe como está para o próximo período)",
      proximoPeriodo(r),
    );
    if (sugerida === null) return;
    try {
      const resp = await api.renovacoes.renovar(r.id, sugerida.trim() || undefined);
      alert(resp?.mensagem ?? "Renovada.");
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Não foi possível renovar.");
    }
  }

  async function excluir(r: any) {
    if (!confirm("Apagar a renovação " + r.titulo + "?")) return;
    try {
      await api.renovacoes.remover(r.id);
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao excluir.");
    }
  }

  const set = (campo: string) => (e: any) => setForm({ ...form, [campo]: e.target.value });

  return (
    <>
      <div className="crud-toolbar">
        <p className="muted ren-intro">
          O que vence e precisa ser renovado. O sistema cria o lembrete sozinho antes do
          vencimento — e, quando você marca "Renovei", já agenda o próximo.
        </p>
        <div className="toolbar-right">
          <button className="btn-primary" onClick={() => setForm({ ...VAZIO })}>
            <IconPlus size={16} /> Nova renovação
          </button>
        </div>
      </div>

      {form && (
        <div className="form-card">
          <div className="form-grid">
            <label className="field field-wide">
              <span>
                O que vence <span className="req">*</span>
              </span>
              <input
                value={form.titulo}
                onChange={set("titulo")}
                placeholder="Ex.: domínio portalsercamp.com"
                autoFocus
              />
            </label>
            <label className="field">
              <span>Tipo</span>
              <select value={form.categoria} onChange={set("categoria")}>
                {CATEGORIAS.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>
                Vence em <span className="req">*</span>
              </span>
              <input type="date" value={form.vence_em ?? ""} onChange={set("vence_em")} />
            </label>
            <label className="field">
              <span>Repete</span>
              <select value={form.repetir} onChange={set("repetir")}>
                {REPETICOES.map((r) => (
                  <option key={r.v} value={r.v}>
                    {r.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Avisar quantos dias antes</span>
              <input type="number" min={0} max={180} value={form.avisar_dias} onChange={set("avisar_dias")} />
            </label>
            <label className="field">
              <span>Onde se renova (fornecedor)</span>
              <input value={form.fornecedor ?? ""} onChange={set("fornecedor")} placeholder="Ex.: KingHost" />
            </label>
            <label className="field">
              <span>Valor (R$)</span>
              <input value={form.valor ?? ""} onChange={set("valor")} inputMode="decimal" />
            </label>
            <label className="field">
              <span>Sistema</span>
              <select value={form.sistema ?? ""} onChange={set("sistema")}>
                <option value="">Geral</option>
                {SISTEMAS_EM_ORDEM.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="field field-wide">
              <span>Link do painel / contrato</span>
              <input value={form.link ?? ""} onChange={set("link")} placeholder="https://…" />
            </label>
            <label className="field field-wide">
              <span>Observação</span>
              <textarea rows={2} value={form.detalhe ?? ""} onChange={set("detalhe")} />
            </label>
          </div>
          <div className="solicitar-foot">
            <button className="btn-secondary btn-sm" onClick={() => setForm(null)} disabled={salvando}>
              Cancelar
            </button>
            <button className="btn-primary btn-sm" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando…" : form.id ? "Salvar" : "Cadastrar"}
            </button>
          </div>
        </div>
      )}

      {erro ? (
        <ErrorState message={erro} onRetry={carregar} />
      ) : carregando ? (
        <div className="carregando">Carregando…</div>
      ) : linhas.length === 0 ? (
        <div className="empty">
          <IconRepeat size={30} />
          Nada cadastrado. Comece pelo domínio e pelo certificado do site — são os que derrubam
          tudo quando vencem.
        </div>
      ) : (
        <ul className="dev-lista dev-lista-cards">
          {linhas.map((r) => (
            <li key={r.id} className={r.ativo ? "" : "dev-passou"}>
              <span className={"pill " + (r.dias < 0 ? "pill-danger" : r.avisando ? "pill-warn" : "pill-neutral")}>
                {dataBr(r.vence_em)}
              </span>
              <span className="dev-lista-main">
                <strong>{r.titulo}</strong>
                <small className="muted">
                  {[
                    quando(r),
                    r.categoria,
                    r.fornecedor,
                    r.sistema ? nomeDoSistema(r.sistema) : "",
                    r.valor ? "R$ " + Number(r.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "",
                    REPETICOES.find((x) => x.v === r.repetir)?.nome,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </small>
                {r.detalhe && <small className="muted">{r.detalhe}</small>}
                {r.link && (
                  <a className="btn-link" href={r.link} target="_blank" rel="noreferrer">
                    abrir painel
                  </a>
                )}
              </span>
              <span className="dev-acoes">
                <button className="icon-btn icon-btn-ok" title="Já renovei" onClick={() => renovar(r)}>
                  <IconCheck size={16} />
                </button>
                <button
                  className="icon-btn"
                  title="Editar"
                  onClick={() => setForm({ ...r, valor: r.valor ?? "", vence_em: String(r.vence_em).slice(0, 10) })}
                >
                  <IconEdit size={16} />
                </button>
                <button className="icon-btn icon-btn-danger" title="Excluir" onClick={() => excluir(r)}>
                  <IconTrash size={16} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** A data que o próximo período cairia — só uma sugestão no campo. */
function proximoPeriodo(r: any): string {
  const d = new Date(String(r.vence_em) + "T12:00:00");
  const meses = { anual: 12, semestral: 6, trimestral: 3, mensal: 1 }[r.repetir as string] ?? 0;
  if (!meses) return String(r.vence_em).slice(0, 10);
  d.setMonth(d.getMonth() + meses);
  return (
    d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0")
  );
}
